// src/pages/MyLearning.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Layout from '../Layout/Layout';
import api from '../utils/apiClient';
import {
  // FiBook,
    FiClock,
    FiCalendar,
    FiCheckCircle,
    FiBarChart2,
    FiArrowRight,
    FiAlertCircle
} from 'react-icons/fi';

// Styled Components
const MyLearningContainer = styled.div`
  min-height: calc(100vh - 70px);
`;

const HeaderSection = styled.section`
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.fonts.sizes['3xl']};
  font-weight: 700;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const PageSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fonts.sizes.lg};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const ProgressSection = styled.section`
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[6]};
  margin-bottom: ${({ theme }) => theme.spacing[8]};
  box-shadow: ${({ theme }) => theme.shadows.subtle};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: ${({ theme }) => theme.colors.gradientPrimary};
  }
`;

const ProgressGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${({ theme }) => theme.spacing[6]};
`;

const ProgressCard = styled.div`
  text-align: center;
`;

const ProgressValue = styled.div`
  font-size: ${({ theme }) => theme.fonts.sizes['2xl']};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  position: relative;
  display: inline-block;
  
  svg {
    font-size: 24px;
    position: absolute;
    top: 2px;
    right: -28px;
  }
`;

const ProgressLabel = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.fonts.sizes['2xl']};
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  display: flex;
  align-items: center;
  
  &::before {
    content: '';
    display: inline-block;
    width: 4px;
    height: 24px;
    background: ${({ theme }) => theme.colors.gradientPrimary};
    margin-right: ${({ theme }) => theme.spacing[3]};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
  }
`;

const CoursesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: ${({ theme }) => theme.spacing[6]};
  margin-bottom: ${({ theme }) => theme.spacing[8]};
`;

const CourseCard = styled(motion.div)`
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.subtle};
  display: flex;
  flex-direction: column;
  transition: all ${({ theme }) => theme.transitions.normal};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${({ theme }) => theme.shadows.cardHover};
    border-color: ${({ theme }) => theme.colors.primary}30;
  }
`;

const CourseImageContainer = styled.div`
  height: 160px;
  background: ${({ gradient, theme }) => gradient || theme.colors.gradientPrimary};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const CourseLetter = styled.div`
  font-size: 72px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.8);
`;

const CourseImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const DifficultyBadge = styled.span`
  position: absolute;
  top: ${({ theme }) => theme.spacing[3]};
  left: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.fonts.sizes.xs};
  font-weight: 600;
  background-color: ${({ difficulty, theme }) => {
        switch (difficulty) {
            case 'beginner': return theme.colors.beginner;
            case 'intermediate': return theme.colors.intermediate;
            case 'advanced': return theme.colors.advanced;
            case 'expert': return theme.colors.expert;
            default: return theme.colors.beginner;
        }
    }};
  color: white;
`;

const ProgressBadge = styled.span`
  position: absolute;
  top: ${({ theme }) => theme.spacing[3]};
  right: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.fonts.sizes.xs};
  font-weight: 600;
  background-color: ${({ progress, theme }) => {
        if (progress === 100) return theme.colors.success;
        if (progress > 50) return theme.colors.primary;
        if (progress > 0) return theme.colors.warning;
        return theme.colors.backgroundLighter;
    }};
  color: ${({ progress, theme }) => progress === 0 ? theme.colors.textSecondary : 'white'};
  display: flex;
  align-items: center;
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[1]};
  }
`;

const CourseContent = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const CourseTitle = styled.h3`
  font-size: ${({ theme }) => theme.fonts.sizes.lg};
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const CourseMetaInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fonts.sizes.xs};
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[1]};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const CourseDescription = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  flex: 1;
  
  /* Multi-line ellipsis for descriptions */
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  overflow: hidden;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${({ value }) => value}%;
    background: ${({ theme }) => theme.colors.gradientPrimary};
    border-radius: ${({ theme }) => theme.borderRadius.full};
    transition: width 0.5s ease;
  }
`;

const CourseActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ProgressText = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
`;

const ContinueButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[4]};
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: 500;
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  svg {
    margin-left: ${({ theme }) => theme.spacing[2]};
    transition: transform ${({ theme }) => theme.transitions.fast};
  }
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryDark};
    
    svg {
      transform: translateX(4px);
    }
  }
`;

const ViewButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[4]};
  background-color: transparent;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: 500;
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  svg {
    margin-left: ${({ theme }) => theme.spacing[2]};
    transition: transform ${({ theme }) => theme.transitions.fast};
  }
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primary}11;
    
    svg {
      transform: translateX(4px);
    }
  }
`;

const EmptyState = styled.div`
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[6]};
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const EmptyStateTitle = styled.h3`
  font-size: ${({ theme }) => theme.fonts.sizes.xl};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const EmptyStateText = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const EmptyStateButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[5]};
  background: ${({ theme }) => theme.colors.gradientPrimary};
  color: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: 600;
  box-shadow: ${({ theme }) => theme.shadows.medium};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  svg {
    margin-left: ${({ theme }) => theme.spacing[2]};
    transition: transform ${({ theme }) => theme.transitions.fast};
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.strong};
    
    svg {
      transform: translateX(4px);
    }
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing[16]} 0;
`;

const LoadingSpinner = styled.div`
  border: 4px solid rgba(10, 255, 112, 0.1);
  border-radius: 50%;
  border-top: 4px solid ${({ theme }) => theme.colors.primary};
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fonts.sizes.lg};
`;

const ErrorMessage = styled.div`
  background-color: ${({ theme }) => theme.colors.error}11;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.error};
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[2]};
    flex-shrink: 0;
  }
`;

const MyLearning = () => {
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [progressStats, setProgressStats] = useState({
    totalCourses: 0,
    completedCourses: 0,
    averageProgress: 0,
    totalHoursLearned: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch enrolled courses
        const enrolledCoursesResponse = await api.courses.getEnrolled();

        // Define courseProgress as a function
        const courseProgress = async (courseId) => {
          if (!courseId) {
            return {};
          }

          try {
            const progressResponse = await api.courses.getProgress(courseId);
            return progressResponse || {};
          } catch (progressError) {
            console.warn(`Failed to fetch progress for course ${courseId}:`, progressError);
            return {};
          }
        };

        const userProgressResponse = await api.courses.getUserProgress();
        // console.log('User Progress Response:', userProgressResponse);

        // Transform the API response to match the expected format
        let transformedEnrollments = [];

        // Check if response is an array (direct list of courses)
        if (Array.isArray(enrolledCoursesResponse)) {
          transformedEnrollments = await Promise.all(
            enrolledCoursesResponse.map(async (course) => {
              const progressData = await courseProgress(course.id);

              return {
                id: course.id,
                course,
                progress_percentage: progressData.progress_percentage ?? progressData.progress ?? 0,
                enrolled_at: progressData.enrolled_at || course.enrolled_at || null,
                ...('course_completed' in progressData ? { course_completed: progressData.course_completed } : {}),
                ...('total_modules' in progressData ? { total_modules: progressData.total_modules } : {}),
                ...('completed_modules' in progressData ? { completed_modules: progressData.completed_modules } : {})
              };
            })
          );
        }
        // Check if it has results array
        else if (enrolledCoursesResponse && enrolledCoursesResponse.results && Array.isArray(enrolledCoursesResponse.results)) {
          transformedEnrollments = await Promise.all(
            enrolledCoursesResponse.results.map(async (enrollment) => {
              const hasProgressValue = typeof enrollment.progress_percentage === 'number' && !Number.isNaN(enrollment.progress_percentage);
              const existingEnrolledAt = enrollment.enrolled_at || null;

              if (hasProgressValue && existingEnrolledAt) {
                return {
                  ...enrollment,
                  progress_percentage: enrollment.progress_percentage
                };
              }

              const courseId = enrollment.course?.id || enrollment.course_id || enrollment.id;
              if (!courseId) {
                return {
                  ...enrollment,
                  progress_percentage: hasProgressValue ? enrollment.progress_percentage : 0,
                  enrolled_at: existingEnrolledAt
                };
              }

              const progressData = await courseProgress(courseId);
              const normalizedProgress = hasProgressValue
                ? enrollment.progress_percentage
                : progressData.progress_percentage ?? progressData.progress ?? 0;

              return {
                ...enrollment,
                progress_percentage: normalizedProgress,
                enrolled_at: existingEnrolledAt || progressData.enrolled_at || null,
                ...('course_completed' in progressData && progressData.course_completed !== undefined
                  ? { course_completed: progressData.course_completed }
                  : {})
              };
            })
          );
        }

        setEnrolledCourses(transformedEnrollments);

        // Calculate progress statistics
        const totalCourses = transformedEnrollments.length;
        const completedCourses = transformedEnrollments.filter(
          enrollment => enrollment.progress_percentage === 100
        ).length;

        // Use backend data if available, otherwise fallback to calculated values
        const averageProgress = userProgressResponse?.average_progress !== undefined
          ? userProgressResponse.average_progress
          : (transformedEnrollments.reduce((sum, enrollment) => sum + (enrollment.progress_percentage || 0), 0) / totalCourses || 0);

        const totalHoursLearned = userProgressResponse?.hours_spent !== undefined
          ? userProgressResponse.hours_spent
          : 0;

        setProgressStats({
          totalCourses,
          completedCourses,
          averageProgress: Math.round(averageProgress || 0),
          totalHoursLearned: Math.round(totalHoursLearned || 0)
        });

        console.log('Final Progress Stats:', {
          totalCourses,
          completedCourses,
          averageProgress: Math.round(averageProgress || 0),
          totalHoursLearned: Math.round(totalHoursLearned || 0)
        });
      } catch (err) {
        console.error('Error fetching enrolled courses:', err);
        setError('Failed to load your learning progress. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return 'Recently';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <Layout>
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>Loading your learning progress...</LoadingText>
        </LoadingContainer>
      </Layout>
    );
  }

  return (
    <Layout>
      <MyLearningContainer>
        <HeaderSection>
          <PageTitle>My Learning</PageTitle>
          <PageSubtitle>Track your progress and continue your cyber security journey</PageSubtitle>

          {error && (
            <ErrorMessage>
              <FiAlertCircle size={20} />
              {error}
            </ErrorMessage>
          )}
        </HeaderSection>

        {enrolledCourses.length > 0 ? (
          <>
            <ProgressSection>
              <ProgressGrid>
                <ProgressCard>
                  <ProgressValue>{progressStats.totalCourses}</ProgressValue>
                  <ProgressLabel>Enrolled Courses</ProgressLabel>
                </ProgressCard>

                <ProgressCard>
                  <ProgressValue>{progressStats.completedCourses}</ProgressValue>
                  <ProgressLabel>Completed Courses</ProgressLabel>
                </ProgressCard>

                <ProgressCard>
                  <ProgressValue>
                    {progressStats.averageProgress}%
                    <FiBarChart2 />
                  </ProgressValue>
                  <ProgressLabel>Average Progress</ProgressLabel>
                </ProgressCard>

                <ProgressCard>
                  <ProgressValue>
                    {progressStats.totalHoursLearned}
                    <FiClock />
                  </ProgressValue>
                  <ProgressLabel>Hours Learned</ProgressLabel>
                </ProgressCard>
              </ProgressGrid>
            </ProgressSection>

            <SectionTitle>Your Enrolled Courses</SectionTitle>

            <CoursesGrid>
              {enrolledCourses.map(enrollment => {
                const course = enrollment.course || {};
                const progressPercentage = enrollment.progress_percentage || 0;

                return (
                  <CourseCard
                    key={enrollment.id || `course-${course.id}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <CourseImageContainer gradient={`linear-gradient(135deg, ${course.gradient_start || '#0AFF70'} 0%, ${course.gradient_end || '#09B6FF'} 100%)`}>
                      {course.thumbnail ? (
                        <CourseImage src={course.thumbnail} alt={course.title || 'Course'} />
                      ) : (
                        <CourseLetter>{course.title ? course.title[0] : 'C'}</CourseLetter>
                      )}

                      <DifficultyBadge difficulty={course.difficulty_level || 'beginner'}>
                        {course.difficulty_level || 'Beginner'}
                      </DifficultyBadge>

                      <ProgressBadge progress={progressPercentage}>
                        {progressPercentage === 100 ? (
                          <><FiCheckCircle /> Completed</>
                        ) : (
                          `${progressPercentage}%`
                        )}
                      </ProgressBadge>
                    </CourseImageContainer>

                    <CourseContent>
                      <CourseTitle>{course.title || 'Untitled Course'}</CourseTitle>

                      <CourseMetaInfo>
                        <MetaItem>
                          <FiClock />
                          {course.duration_text || `${course.estimated_duration || 0} hours`}
                        </MetaItem>

                        <MetaItem>
                          <FiCalendar />
                          Enrolled on {formatDate(enrollment.enrolled_at)}
                        </MetaItem>
                      </CourseMetaInfo>

                      <CourseDescription>{course.description || 'No description available'}</CourseDescription>

                      <ProgressBar value={progressPercentage} />

                      <CourseActions>
                        <ProgressText>{progressPercentage}% Complete</ProgressText>

                        {progressPercentage === 100 ? (
                          <ViewButton to={`/courses/${course.id}`}>
                            View Course <FiArrowRight />
                          </ViewButton>
                        ) : (
                          <ContinueButton to={`/learning/${course.id}`}>
                            Continue <FiArrowRight />
                          </ContinueButton>
                        )}
                      </CourseActions>
                    </CourseContent>
                  </CourseCard>
                );
              })}
            </CoursesGrid>
          </>
        ) : (
          <EmptyState>
            <EmptyStateTitle>You haven't enrolled in any courses yet!</EmptyStateTitle>
            <EmptyStateText>
              Explore our catalog and start your cyber security journey today.
            </EmptyStateText>
            <EmptyStateButton to="/courses">
              Browse Courses <FiArrowRight />
            </EmptyStateButton>
          </EmptyState>
        )}
      </MyLearningContainer>
    </Layout>
  );
};
export default MyLearning;